import { Box } from "@mui/material";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { WidgetHeader } from "components/atoms/WidgetHeader";
import { WidgetPaper } from "components/atoms/WidgetPaper";
import { CloudDataGrid } from "components/modules/CloudDataGrid";
import { OrganizationCell } from "components/modules/tableCells/OrganizationCell";
import { OrganizationStatusCell } from "components/modules/tableCells/OrganizationStatusCell";
import { OrgDisinfectionCountCell } from "components/modules/tableCells/OrgDisinfectionCountCell";
import { OwnerCell } from "components/modules/tableCells/OwnerCell";
import { graphql } from "gql";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useQueryWithSnack } from "utils/useQueryWithSnack";
import { PushButton } from "../atoms/buttons/PushButton";
import { CreateOrganization } from "./dialogs/CreateOrganization";
import { getOrganizationAdmins } from "utils/affiliationUtils";
import { Identity } from "gql/graphql";
import {
  getOrganizationOwnerInfo,
  getOrganizationStatus,
} from "utils/privileges/privilegeUtils";

export const ORG_LIST_QUERY = graphql(`
  query Organizations($pagination: Pagination) {
    organizations(pagination: $pagination) {
      id
      name
      robotCount
      robotAffiliations {
        robot {
          serialNumber
          active {
            organization {
              id
            }
          }
        }
      }
      country
      activationState
      identityCount
      affiliations {
        identity {
          id
          firstName
          lastName
          email
        }
        roles {
          title
        }
      }
    }
  }
`);

export const ORG_COUNT_QUERY = graphql(`
  query OrganizationsCount {
    organizationCount
  }
`);

export const ORGS_DISINFECTION_COUNTS_QUERY = graphql(`
  query OrgsDisinfectionCounts($orgIds: [String!]!) {
    disinfectionsPerOrganization(orgIds: $orgIds) {
      orgsDisinfectionCounts
    }
  }
`);

const columns: GridColDef[] = [
  {
    field: "name",
    maxWidth: 300,
    minWidth: 150,
    flex: 1,
    headerName: "Name",
    valueGetter: (params) => params.row?.name,
    renderCell: (params) => <OrganizationCell organization={params.row} />,
  },
  {
    field: "owner",
    maxWidth: 300,
    flex: 1,
    headerName: "Owner",
    sortable: true,
    valueGetter: (params) => {
      const affiliations = params.row.affiliations || [];
      const owners = getOrganizationAdmins(affiliations).owners;
      const firstOwner = owners[0] as unknown as Identity;
      return firstOwner
        ? `${firstOwner.firstName} ${firstOwner.lastName}`.toLowerCase()
        : "";
    },
    renderCell: OwnerCell,
  },
  {
    field: "robots",
    maxWidth: 80,
    flex: 1,
    headerName: "Robots",
    align: "right",
    headerAlign: "right",
    valueGetter(params) {
      return params.row.robotCount || 0;
    },
  },
  {
    field: "users",
    maxWidth: 80,
    flex: 1,
    headerName: "Users",
    align: "right",
    headerAlign: "right",
    valueGetter(params) {
      return params.row.identityCount || 0;
    },
  },
  {
    field: "disinfections",
    align: "right",
    headerAlign: "right",
    maxWidth: 180,
    flex: 1,
    headerName: "Disinfections",
    valueGetter: (params) => params.row.disinfectionCount,
    renderCell: OrgDisinfectionCountCell,
  },
  {
    field: "status",
    maxWidth: 100,
    flex: 1,
    headerName: "Status",
    valueGetter: (params) => {
      const ownerInfo = getOrganizationOwnerInfo(params.row?.affiliations);
      const orgStatus = getOrganizationStatus(
        params.row.activationState,
        ownerInfo.hasOwner,
      );
      return orgStatus;
    },
    renderCell: OrganizationStatusCell,
  },
];

export const Organizations = () => {
  const navigate = useNavigate();
  const [createOrgDialogOpen, setCreateOrgDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const { data: countData } = useQueryWithSnack(ORG_COUNT_QUERY);

  const pageSize = 25;

  const { data: orgListDataInitial, loading } =
    useQueryWithSnack(ORG_LIST_QUERY);

  const { data: orgsDisinfectionCountsData } = useQueryWithSnack(
    ORGS_DISINFECTION_COUNTS_QUERY,
    {
      variables: {
        orgIds:
          orgListDataInitial?.organizations.map((item: any) => item.id) || [],
      },
      skip: !orgListDataInitial?.organizations?.length, // No need to fetch if no orgs
    },
  );

  const orgListData = {
    organizations:
      orgListDataInitial?.organizations?.map((org: any) => ({
        ...org,
        disinfectionCount:
          orgsDisinfectionCountsData?.disinfectionsPerOrganization
            ?.orgsDisinfectionCounts?.[org.id] || 0,
      })) || [],
  };

  const handleNavigate = (params: GridRowParams) => {
    navigate(`/cloud/support/organizations/${params.row.id}`);
  };

  return (
    <>
      <WidgetPaper>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <WidgetHeader
            label={
              <FormattedMessage
                id="organizations"
                defaultMessage="Organizations"
              />
            }
          />

          <PushButton
            onClick={() => setCreateOrgDialogOpen(true)}
            variant="outlined"
            size="small"
          >
            New organization
          </PushButton>
        </Box>
        <CloudDataGrid
          onRowClick={handleNavigate}
          rows={orgListData?.organizations || []}
          columns={columns}
          loading={loading}
          initialState={{
            sorting: {
              sortModel: [{ field: "name", sort: "asc" }],
            },
          }}
          page={page}
          onPageChanged={(pageNumber) => {
            setPage(pageNumber);
          }}
          pageSize={pageSize}
          rowCount={countData?.organizationCount ?? 0}
        />
      </WidgetPaper>
      <CreateOrganization
        open={createOrgDialogOpen}
        onClose={() => setCreateOrgDialogOpen(false)}
      />
    </>
  );
};
